import React, { useCallback, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import useModelStore from '../../store/models-store';
import useCrudListPage from '../../shared/hooks/useCrudListPage';
import DataTable from '../../shared/components/table/DataTable';
import PageHeader from '../../shared/components/table/PageHeader';
import SearchInput from '../../shared/components/table/SearchInput';
import RowActions from '../../shared/components/table/RowActions';
import ResourceFormModal from '../../shared/components/modal/ResourceFormModal';
import DeleteModal from '../../shared/components/modal/DeleteModal';
import { providers } from '../../shared/config/constants';

const columnHelper = createColumnHelper();

const modelFields = [
  {
    name: 'provider',
    label: 'Provider',
    type: 'select',
    required: true,
    options: providers,
    placeholder: 'Select Provider',
  },
  { name: 'name', label: 'Name', type: 'text', required: true },
  { name: 'temperature', label: 'Temperature', type: 'number' },
  { name: 'max_tokens', label: 'Max Tokens', type: 'number' },
  {
    name: 'reasoning_effort',
    label: 'Reasoning Effort',
    type: 'select',
    options: [
      { value: 'high', label: 'High' },
      { value: 'medium', label: 'Medium' },
      { value: 'low', label: 'Low' },
    ],
    placeholder: 'Select',
  },
  { name: 'budget_tokens', label: 'Budget Tokens', type: 'number' },
];

const emptyModel = {
  provider: '',
  name: '',
  temperature: null,
  max_tokens: null,
  reasoning_effort: '',
  budget_tokens: null,
};

const ModelsPage = () => {
  const { models, fetchModels, createModel, updateModel, deleteModel } = useModelStore();
  const openEditRef = React.useRef(() => {});
  const openDeleteRef = React.useRef(() => {});

  const fetchModelsFresh = useCallback(() => fetchModels({ force: true }), [fetchModels]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'ID',
        cell: (info) => (info.getValue() ? info.getValue().split('-')[0] : '-'),
      }),
      columnHelper.accessor('provider', { header: 'Provider' }),
      columnHelper.accessor('name', { header: 'Name' }),
      columnHelper.accessor('temperature', {
        header: 'Temperature',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
      }),
      columnHelper.accessor('max_tokens', {
        header: 'Max Tokens',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
      }),
      columnHelper.accessor('budget_tokens', {
        header: 'Budget Tokens',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
      }),
      columnHelper.accessor('reasoning_effort', {
        header: 'Reasoning Effort',
        cell: (info) => (info.getValue() ? info.getValue() : '-'),
      }),
      columnHelper.accessor('actions', {
        header: 'Actions',
        cell: ({ row }) => (
          <RowActions
            onEdit={() => openEditRef.current(row.original)}
            onDelete={() => openDeleteRef.current(row.original)}
          />
        ),
      }),
    ],
    []
  );

  const sortItems = React.useCallback(
    (items) => [...items].sort((a, b) => a.name.localeCompare(b.name)),
    []
  );

  const {
    table,
    isLoading,
    globalFilter,
    setGlobalFilter,
    isFormOpen,
    formMode,
    isDeleteModalOpen,
    selectedItem,
    deletePayload,
    openCreate,
    openEdit,
    openDelete,
    closeModals,
    handleFormSubmit,
    handleDelete,
  } = useCrudListPage({
    items: models,
    fetchItems: fetchModelsFresh,
    createItem: createModel,
    updateItem: updateModel,
    deleteItem: deleteModel,
    columns,
    sortItems,
    entityLabel: 'Model',
    getDeletePayload: (item) => ({ ...item, dataName: 'model' }),
  });

  openEditRef.current = openEdit;
  openDeleteRef.current = openDelete;

  const initialValues = useMemo(() => {
    if (formMode === 'edit' && selectedItem) {
      return {
        provider: selectedItem.provider || '',
        name: selectedItem.name || '',
        temperature: selectedItem.temperature ?? null,
        max_tokens: selectedItem.max_tokens ?? null,
        reasoning_effort: selectedItem.reasoning_effort || '',
        budget_tokens: selectedItem.budget_tokens ?? null,
      };
    }
    return emptyModel;
  }, [formMode, selectedItem]);

  return (
    <div className="max-w-screen-3xl mx-auto mt-4">
      <PageHeader title="Models List" onCreate={openCreate} />
      <div className="mb-4">
        <SearchInput value={globalFilter} onChange={setGlobalFilter} />
      </div>
      <DataTable
        table={table}
        columnsCount={columns.length}
        isLoading={isLoading}
        emptyMessage="No models found"
      />
      <ResourceFormModal
        isOpen={isFormOpen}
        onClose={closeModals}
        mode={formMode}
        title={formMode === 'edit' ? 'Edit Model' : 'Create Model'}
        fields={modelFields}
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        gridClassName="grid grid-cols-2 gap-4"
      />
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={closeModals}
        data={deletePayload}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default ModelsPage;
